import { useDeferredValue, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { HrSearchItem } from '@snaphire/contracts';
import { useRows } from './api/data';
import type { GetPath, Row } from './api/client';
import { Avatar, Button, ErrorState, Icon, Loading, Overlay } from './ui';

const searchGroups: {
  type: HrSearchItem['type'];
  title: string;
  resource?: string;
  icon: string;
}[] = [
  { type: 'Employee', title: 'Сотрудники', icon: 'People' },
  { type: 'Team', title: 'Проекты', resource: 'teams', icon: 'People' },
  { type: 'Department', title: 'Отделы', resource: 'departments', icon: 'Bank' },
  { type: 'Position', title: 'Должности', resource: 'positions', icon: 'NationalId' },
  { type: 'Location', title: 'Локации', resource: 'locations', icon: 'LocationPin' },
];
export function HrGlobalSearch({ onClose }: { onClose: () => void }) {
  const [search, setSearch] = useState('');
  const query = useDeferredValue(search.trim());
  const results = useRows<HrSearchItem & Row>(
    '/api/v1/search' as GetPath,
    { q: query, limit: 30 },
    { enabled: query.length >= 2 },
  );
  const navigate = useNavigate();
  return (
    <Overlay title="Поиск" variant="dialog" onClose={onClose}>
      <div className="global-search-input">
        <Icon name="Search" />
        <input
          autoFocus
          aria-label="Поиск"
          placeholder="Сотрудник, проект, отдел или должность"
          value={search}
          maxLength={100}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      <div className="search-results">
        {query.length < 2 ? (
          <div className="table-empty">Введите хотя бы два символа</div>
        ) : results.isLoading ? (
          <Loading />
        ) : results.error ? (
          <ErrorState error={results.error} />
        ) : (
          searchGroups.map((group) => {
            const items = results.rows.filter((item) => item.type === group.type);
            if (!items.length) return null;
            return (
              <div key={group.type}>
                <h3>{group.title}</h3>
                {items.map((item) => (
                  <button
                    type="button"
                    aria-label={`${item.title}${item.subtitle ? `, ${item.subtitle}` : ''}`}
                    key={item.id}
                    onClick={() => {
                      if (group.type === 'Employee')
                        void navigate({
                          to: '/employees/profile/$employeeId',
                          params: { employeeId: item.id },
                        });
                      else
                        void navigate({
                          to: '/$section/$resource/$entityId',
                          params: {
                            section: 'organisation',
                            resource: group.resource!,
                            entityId: item.id,
                          },
                        });
                      onClose();
                    }}
                  >
                    {group.type === 'Employee' ? (
                      <Avatar label={item.title} />
                    ) : (
                      <Icon name={group.icon} />
                    )}
                    <span>
                      {item.title}
                      {item.subtitle ? <small>{item.subtitle}</small> : null}
                    </span>
                    <Icon name="ChevronRight" size={16} />
                  </button>
                ))}
              </div>
            );
          })
        )}
        {query.length >= 2 && !results.isLoading && !results.error && !results.rows.length ? (
          <div className="table-empty">Ничего не найдено</div>
        ) : null}
        {results.hasNextPage && (
          <Button
            disabled={results.isFetchingNextPage}
            onClick={() => void results.fetchNextPage()}
          >
            Показать ещё
          </Button>
        )}
      </div>
    </Overlay>
  );
}
